import type { Middleware } from '@reduxjs/toolkit'
import axiosInstance from 'api/axiosConfig'
import * as offlineQueue from 'store/offlineQueue'
import * as db from 'store/db'
import { invalidateProductDetail, seedProductDetailsFromList } from 'services/productDetailCache'
import { getLastKnownOnline } from 'utils/connectivityCheck'
import { formDataHasFiles, formDataToPlainObject } from 'helpers/offlineFormData'
import { fetchCategories } from 'slices/categoriesSlice'
import { fetchProducts } from 'slices/productsSlice'
import { fetchCustomers } from 'slices/customersSlice'
import { fetchSuppliers } from 'slices/suppliersSlice'
import { fetchPurchases } from 'slices/purchasesSlice'
import { fetchExpenses } from 'slices/expensesSlice'
import { fetchDeliveries, fetchActiveDrivers, fetchTodayDeliveries } from 'slices/deliveriesSlice'
import { fetchDeliveryZones } from 'slices/deliveryZonesSlice'
import { fetchDriverSettlements } from 'slices/driverSettlementsSlice'

/**
 * طابور الكتابة عند انقطاع الشبكة (Redux offline queue).
 * غير مغطى هنا ويتطلب اتصالاً حتى يُنفَّذ الطلب: موظفين، إشعارات، بطاقات هدايا، فئات/مصروفات متكررة، إلخ.
 * FormData بدون ملفات يُحوَّل لكائن ويُؤجَّل؛ الطلبات التي تحتوي ملفات تُرفض أوفلاين برسالة واضحة.
 */
const WRITE_ACTIONS = [
 'categories/createCategory',
 'categories/updateCategory',
 'categories/deleteCategory',
 'products/createProduct',
 'products/updateProduct',
 'products/deleteProduct',
 'customers/createCustomer',
 'customers/updateCustomer',
 'customers/deleteCustomer',
 'customers/addBalance',
 'suppliers/createSupplier',
 'suppliers/updateSupplier',
 'suppliers/deleteSupplier',
 'purchases/createPurchase',
 'purchases/updatePurchase',
 'purchases/deletePurchase',
 'expenses/createExpense',
 'expenses/cancelExpense',
 'deliveries/assignDriver',
 'deliveries/updateStatus',
 'deliveries/createDriver',
 'deliveries/updateDriver',
 'deliveries/toggleDriver',
 'deliveryZones/createDeliveryZone',
 'deliveryZones/updateDeliveryZone',
 'driverSettlements/createSettlement',
]

const CACHE_THUNKS: Record<string, db.CacheKey> = {
 'categories/fetchCategories': 'categories',
 'products/fetchProducts': 'products',
 'customers/fetchCustomers': 'customers',
 'suppliers/fetchSuppliers': 'suppliers',
 'purchases/fetchPurchases': 'purchases',
 'expenses/fetchExpenses': 'expenses',
 'deliveries/fetchDeliveries': 'deliveries',
 // NOTE: thunk prefixes come from deliveriesSlice.ts createAsyncThunk first arg
 // fetchTodayDeliveries → 'deliveries/fetchToday' (not the export name)
 // fetchActiveDrivers   → 'deliveries/fetchDrivers' (not the export name)
 //
 // IMPORTANT: 'deliveryTrackingToday' is the slice/middleware cache (shape: {summary, deliveries: [...]}).
 // It is INTENTIONALLY separate from 'deliveryTracking' which is used by useDeliveryTrackingPoll
 // for the lane-based board shape ({summary, lanes, completed_recent, filters?}).
 // Mixing them corrupts both consumers.
 'deliveries/fetchToday': 'deliveryTrackingToday',
 'deliveries/fetchDrivers': 'activeDrivers',
 'deliveryZones/fetchDeliveryZones': 'deliveryZones',
}

const API_MAP: Record<string, { method: string; getUrl: (action: any) => string; getData: (action: any) => any }> = {
 'categories/createCategory': { method: 'POST', getUrl: () => '/categories', getData: (a) => a.meta?.arg },
 'categories/updateCategory': { method: 'POST', getUrl: (a) => `/categories/${a.meta?.arg?.id}`, getData: (a) => ({ ...a.meta?.arg?.data, _method: 'PUT' }) },
 'categories/deleteCategory': { method: 'DELETE', getUrl: (a) => `/categories/${a.meta?.arg}`, getData: () => undefined },
 'products/createProduct': { method: 'POST', getUrl: () => '/products', getData: (a) => a.meta?.arg },
 'products/updateProduct': { method: 'POST', getUrl: (a) => `/products/${a.meta?.arg?.id}`, getData: (a) => ({ ...a.meta?.arg?.data, _method: 'PUT' }) },
 'products/deleteProduct': { method: 'DELETE', getUrl: (a) => `/products/${a.meta?.arg}`, getData: () => undefined },
 'customers/createCustomer': { method: 'POST', getUrl: () => '/customers', getData: (a) => a.meta?.arg },
 'customers/updateCustomer': { method: 'PUT', getUrl: (a) => `/customers/${a.meta?.arg?.id}`, getData: (a) => a.meta?.arg?.data },
 'customers/deleteCustomer': { method: 'DELETE', getUrl: (a) => `/customers/${a.meta?.arg}`, getData: () => undefined },
 'customers/addBalance': {
 method: 'POST',
 getUrl: (a) => `/customers/${a.meta?.arg?.id}/balance`,
 getData: (a) => ({ amount: a.meta?.arg?.amount, type: a.meta?.arg?.type, notes: a.meta?.arg?.notes }),
 },
 'suppliers/createSupplier': { method: 'POST', getUrl: () => '/suppliers', getData: (a) => a.meta?.arg },
 'suppliers/updateSupplier': { method: 'PUT', getUrl: (a) => `/suppliers/${a.meta?.arg?.id}`, getData: (a) => a.meta?.arg?.data },
 'suppliers/deleteSupplier': { method: 'DELETE', getUrl: (a) => `/suppliers/${a.meta?.arg}`, getData: () => undefined },
 'purchases/createPurchase': { method: 'POST', getUrl: () => '/purchases', getData: (a) => a.meta?.arg },
 'purchases/updatePurchase': { method: 'PUT', getUrl: (a) => `/purchases/${a.meta?.arg?.id}`, getData: (a) => a.meta?.arg?.data },
 'purchases/deletePurchase': { method: 'DELETE', getUrl: (a) => `/purchases/${a.meta?.arg}`, getData: () => undefined },
 'expenses/createExpense': { method: 'POST', getUrl: () => '/expenses', getData: (a) => a.meta?.arg },
 'expenses/cancelExpense': { method: 'POST', getUrl: (a) => `/expenses/${a.meta?.arg}/cancel`, getData: () => undefined },
 'deliveries/assignDriver': {
 method: 'POST',
 getUrl: (a) => `/deliveries/${a.meta?.arg?.id}/assign`,
 getData: (a) => ({ driver_id: a.meta?.arg?.driver_id }),
 },
 'deliveries/updateStatus': {
 method: 'PATCH',
 getUrl: (a) => `/deliveries/${a.meta?.arg?.id}/status`,
 getData: (a) => {
 const { id: _id, ...rest } = a.meta?.arg ?? {}
 return rest
 },
 },
  'deliveries/createDriver': { method: 'POST', getUrl: () => '/drivers', getData: (a) => a.meta?.arg },
  'deliveries/updateDriver': {
  method: 'PUT',
  getUrl: (a) => `/drivers/${a.meta?.arg?.id}`,
  getData: (a) => a.meta?.arg?.data ?? a.meta?.arg,
  },
   'deliveries/toggleDriver': {
   method: 'POST',
   getUrl: (a) => `/drivers/${a.meta?.arg}/toggle`,
   getData: () => undefined,
   },
   'deliveryZones/createDeliveryZone': { method: 'POST', getUrl: () => '/delivery-zones', getData: (a) => a.meta?.arg },
   'deliveryZones/updateDeliveryZone': {
   method: 'PUT',
   getUrl: (a) => `/delivery-zones/${a.meta?.arg?.id}`,
   getData: (a) => a.meta?.arg?.data ?? a.meta?.arg,
   },
   'driverSettlements/createSettlement': {
   method: 'POST',
   getUrl: () => '/driver-settlements',
   getData: (a) => a.meta?.arg,
   },
}

function stripMethodField<T extends Record<string, unknown>>(obj: T): Omit<T, '_method'> {
 const { _method: _m, ...rest } = obj as T & { _method?: unknown }
 return rest as Omit<T, '_method'>
}

/** Replay-safe: bind queued POST body to the mutation idempotency key as client_uuid. */
function withOfflineClientUuid(data: unknown, idempotencyKey: string): unknown {
 if (!data || typeof data !== 'object' || data instanceof FormData || Array.isArray(data)) {
 return data
 }
 const row = data as Record<string, unknown>
 if (typeof row.client_uuid === 'string' && row.client_uuid.trim() !== '') {
 return data
 }
 return { ...row, client_uuid: idempotencyKey }
}

/** حمولة fulfilled متفائلة تتوافق مع extraReducers لكل شريحة. */
function buildOptimisticFulfilledPayload(storeApi: { getState: () => unknown }, actionBase: string, action: any, getData: (a: any) => any): any {
 const arg = action.meta?.arg
 const state = storeApi.getState() as Record<string, any>

 const deletePayloadTypes = [
 'categories/deleteCategory',
 'products/deleteProduct',
 'customers/deleteCustomer',
 'suppliers/deleteSupplier',
 'purchases/deletePurchase',
 ]
 if (deletePayloadTypes.includes(actionBase)) {
 return arg
 }

 if (actionBase === 'expenses/cancelExpense') {
 return arg
 }

 if (actionBase === 'categories/updateCategory' && arg?.id != null) {
 const existing = state.categories?.data?.find((c: { id: number }) => c.id === arg.id)
 const patch = stripMethodField({ ...(arg.data ?? {}) })
 return existing ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true } : { id: arg.id, ...patch, _offlineOptimistic: true }
 }

 if (actionBase === 'products/updateProduct' && arg?.id != null) {
 const existing = state.products?.data?.find((p: { id: number }) => p.id === arg.id)
 const patch = stripMethodField({ ...(arg.data ?? {}) })
 return existing ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true } : { id: arg.id, ...patch, _offlineOptimistic: true }
 }

 if (actionBase === 'customers/updateCustomer' && arg?.id != null) {
 const existing = state.customers?.data?.find((c: { id: number }) => c.id === arg.id)
 const patch = arg.data ?? {}
 return existing ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true } : { id: arg.id, ...patch, _offlineOptimistic: true }
 }

 if (actionBase === 'suppliers/updateSupplier' && arg?.id != null) {
 const existing = state.suppliers?.data?.find((s: { id: number }) => s.id === arg.id)
 const patch = arg.data ?? {}
 return existing ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true } : { id: arg.id, ...patch, _offlineOptimistic: true }
 }

 if (actionBase === 'purchases/updatePurchase' && arg?.id != null) {
 const existing = state.purchases?.data?.find((p: { id: number }) => p.id === arg.id)
 const patch = arg.data ?? {}
 return existing ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true } : { id: arg.id, ...patch, _offlineOptimistic: true }
 }

 if (actionBase === 'deliveries/assignDriver' && arg?.id != null) {
 const st = state.deliveries
 const existing = st?.data?.find((d: { id: string }) => d.id === arg.id)
 const driver = st?.drivers?.find((d: { id: string }) => d.id === arg.driver_id)
 return existing
 ? { ...existing, driver_id: arg.driver_id, driver: driver ?? existing.driver, _offlineOptimistic: true }
 : { id: arg.id, driver_id: arg.driver_id, driver, _offlineOptimistic: true }
 }

 if (actionBase === 'deliveries/updateStatus' && arg?.id != null) {
 const { id, ...rest } = arg
 const existing = state.deliveries?.data?.find((d: { id: string }) => d.id === id)
 return existing ? { ...existing, ...rest, id, _offlineOptimistic: true } : { id, ...rest, _offlineOptimistic: true }
 }

 if (actionBase === 'deliveries/updateDriver' && arg?.id != null) {
 const st = state.deliveries
 const existing = st?.drivers?.find((d: { id: string }) => d.id === arg.id)
 const patch = arg.data ?? {}
 return existing
 ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true }
 : { ...patch, id: arg.id, _offlineOptimistic: true }
 }

 if (actionBase === 'deliveries/toggleDriver') {
 const st = state.deliveries
 const existing = st?.drivers?.find((d: { id: string }) => d.id === arg)
 if (!existing) return { id: arg, _offlineOptimistic: true }
 return {
 ...existing,
 is_active: !existing.is_active,
 _offlineOptimistic: true,
 }
 }

 if (actionBase === 'deliveries/createDriver') {
 return { ...arg, _offlineOptimistic: true }
 }

 if (actionBase === 'deliveryZones/updateDeliveryZone' && arg?.id != null) {
 const existing = state.deliveryZones?.data?.find((z: { id: string }) => z.id === arg.id)
 const patch = arg.data ?? {}
 return existing
 ? { ...existing, ...patch, id: arg.id, _offlineOptimistic: true }
 : { ...patch, id: arg.id, _offlineOptimistic: true }
 }

 if (actionBase === 'deliveryZones/createDeliveryZone') {
 return { ...arg, _offlineOptimistic: true }
 }

 if (actionBase === 'customers/addBalance' && arg?.id != null) {
 return { ...arg, _offlineOptimistic: true }
 }

 const raw = getData(action)
 if (raw != null && typeof raw === 'object' && !(raw instanceof FormData) && !Array.isArray(raw)) {
 return { ...raw, _offlineOptimistic: true }
 }
 if (arg != null && typeof arg === 'object' && !(arg instanceof FormData)) {
 return { ...arg, _offlineOptimistic: true }
 }
 return raw
}

/** Queued admin customer create that replayed against POST /customers. */
export function isQueuedCustomerCreate(mutation: {
 actionType?: string
 method?: string
 url?: string
}): boolean {
 if (mutation.actionType === 'customers/createCustomer') return true
 const method = String(mutation.method || '').toUpperCase()
 if (method !== 'POST') return false
 const path = String(mutation.url || '').split('?')[0].replace(/\/+$/, '')
 return path === '/customers' || path.endsWith('/customers')
}

/** 422 from CustomerController::store when the phone already belongs to a customer. */
export function isCustomerPhoneDuplicateError(error: unknown): boolean {
 const e = error as {
 response?: {
 status?: number
 data?: {
 existing_customer_id?: number
 errors?: { phone?: string[] }
 message?: string
 }
 }
 }
 if (e?.response?.status !== 422) return false
 const data = e.response?.data
 if (data?.existing_customer_id != null) return true
 const phoneErr = Array.isArray(data?.errors?.phone) ? data.errors.phone[0] : undefined
 return isDuplicatePhoneMessage(data?.message) || isDuplicatePhoneMessage(phoneErr)
}

function isDuplicatePhoneMessage(message: unknown): boolean {
 const msg = String(message ?? '')
 return /already exists/i.test(msg) || msg.includes('مسجل بنفس رقم') || /existe déjà/i.test(msg)
}

function markCancelledExpenseInCache(expenseId: number): void {
 void db.getCache<any>('expenses').then((cached) => {
 const payload = cached?.data
 const expenses = payload?.expenses
 if (!Array.isArray(expenses)) return

 const cancelled = expenses.find((expense: { id?: number }) => Number(expense.id) === expenseId)
 if (!cancelled) return

 const wasCancelled = cancelled.status === 'cancelled'
 const amount = wasCancelled ? 0 : Number(cancelled.amount || 0)
 const summary = payload.summary ?? {}

 return db.setCache('expenses', {
 ...payload,
 expenses: expenses.map((expense: { id?: number }) => (
 Number(expense.id) === expenseId ? { ...expense, status: 'cancelled' } : expense
 )),
 summary: {
 ...summary,
 total: Math.max(0, Number(summary.total || 0) - amount),
 count: Math.max(0, Number(summary.count || 0) - (wasCancelled ? 0 : 1)),
 },
 })
 }).catch(() => {})
}

export const offlineMiddleware: Middleware = (storeApi) => (next) => (action: any) => {
 if (action?.type?.endsWith('/fulfilled')) {
 const cacheKey = CACHE_THUNKS[action.type]
 const latestExpenseRequestId = (storeApi.getState() as any).expenses?.activeRequestId
 const isStaleExpenseFetch = action.type === 'expenses/fetchExpenses/fulfilled'
 && Boolean(latestExpenseRequestId)
 && latestExpenseRequestId !== action.meta?.requestId
 if (cacheKey && action.payload != null && !isStaleExpenseFetch) {
 const payload = action.payload
 const data = payload?.data ?? payload?.result ?? payload
 if (Array.isArray(data) || (data && typeof data === 'object')) {
 db.setCache(cacheKey, payload).catch(() => {})
 }

 if (action.type === 'products/fetchProducts/fulfilled') {
 const list = Array.isArray(payload?.data)
 ? payload.data
 : Array.isArray(data)
 ? data
 : []
 seedProductDetailsFromList(list)
 }
 }

 if (action.type === 'expenses/cancelExpense/fulfilled') {
 const expenseId = Number(action.payload)
 if (Number.isFinite(expenseId)) markCancelledExpenseInCache(expenseId)
 }

 if (action.type === 'products/createProduct/fulfilled' || action.type === 'products/updateProduct/fulfilled') {
 const product = action.payload as { id?: number }
 if (product?.id) void invalidateProductDetail(Number(product.id))
 }
 if (action.type === 'products/deleteProduct/fulfilled') {
 const payload = action.payload as number | { id?: number }
 const deletedId = typeof payload === 'number' ? payload : payload?.id
 if (deletedId) void invalidateProductDetail(Number(deletedId))
 }
 }

 if (action?.type?.endsWith('/rejected')) {
 const isNetworkError =
 !action?.payload &&
 action?.meta?.baseQueryMeta == null &&
 action?.error?.message?.includes('Network Error')

 if (!isNetworkError && action?.meta?.baseQueryMeta == null) {
 const originalType = action?.meta?.requestStatus === 'rejected' ? action.type.replace('/rejected', '') : null
 if (!originalType) {
 const errorAny = action?.error || action?.payload
 if (errorAny && typeof errorAny === 'object' && 'message' in errorAny) {
 // not a network error we want to queue
 }
 }
 }
 }

 if (action?.type?.endsWith('/pending')) {
 const actionBase = action.type.replace('/pending', '')
 const apiConfig = API_MAP[actionBase]

 if (apiConfig && !getLastKnownOnline() && WRITE_ACTIONS.includes(actionBase)) {
 const url = apiConfig.getUrl(action)
 let rawData = apiConfig.getData(action)

 if (rawData instanceof FormData) {
 if (formDataHasFiles(rawData)) {
 return next({
 type: action.type.replace('/pending', '/rejected'),
 payload: 'لا يمكن حفظ الملفات بدون اتصال بالإنترنت.',
 meta: { ...action.meta, requestStatus: 'rejected', aborted: true },
 error: { message: 'offline_file_upload' },
 })
 }
 try {
 rawData = formDataToPlainObject(rawData)
 } catch {
 return next({
 type: action.type.replace('/pending', '/rejected'),
 payload: 'لا يمكن حفظ هذا الطلب بدون اتصال.',
 meta: { ...action.meta, requestStatus: 'rejected', aborted: true },
 error: { message: 'offline_form_data' },
 })
 }
 }
 const method =
 apiConfig.method === 'POST' && rawData && typeof rawData === 'object' && (rawData as { _method?: string })._method === 'PUT'
 ? ('PUT' as const)
 : (apiConfig.method.toUpperCase() as 'POST' | 'PUT' | 'DELETE' | 'PATCH')

 const existingUuid =
 rawData && typeof rawData === 'object' && !(rawData instanceof FormData) && !Array.isArray(rawData)
 ? (rawData as { client_uuid?: string }).client_uuid
 : undefined
 const idempotencyKey =
 typeof existingUuid === 'string' && existingUuid.trim() !== '' ? existingUuid : crypto.randomUUID()
 rawData = withOfflineClientUuid(rawData, idempotencyKey)
 const payloadBranchId =
 rawData && typeof rawData === 'object' && !(rawData instanceof FormData) && !Array.isArray(rawData)
 ? (rawData as { branch_id?: unknown }).branch_id
 : undefined

 offlineQueue
 .enqueue({
 url,
 method,
 data: rawData,
 idempotency_key: idempotencyKey,
 branch_id: typeof payloadBranchId === 'string' && payloadBranchId.trim() !== ''
 ? payloadBranchId
 : undefined,
 sliceName: actionBase.split('/')[0],
 actionType: actionBase,
 })
 .catch(() => {})

 const optimisticPayload = buildOptimisticFulfilledPayload(storeApi, actionBase, action, apiConfig.getData)

 return next({
 type: action.type.replace('/pending', '/fulfilled'),
 payload: optimisticPayload,
 meta: { ...action.meta, offlineQueued: true },
 })
 }
 }

 return next(action)
}

export async function replayQueue(): Promise<{ replayed: number; errors: string[] }> {
 const queue = await offlineQueue.getQueue()
 if (queue.length === 0) return { replayed: 0, errors: [] }

 const errors: string[] = []
 const done = new Set<string>()
 const succeededSlices = new Set<string>()
 const permanentlyFailedSlices = new Set<string>()

 for (const mutation of queue) {
  if (mutation.status === 'failed') continue
  if (!getLastKnownOnline()) break

  try {
   const config: any = {}
   if (mutation.data && mutation.method !== 'DELETE') {
    if (mutation.data instanceof FormData) {
     config.data = mutation.data
     config.headers = { 'Content-Type': 'multipart/form-data' }
    } else {
     const idKey = mutation.idempotency_key ?? mutation.local_id ?? mutation.id
     const replayData = idKey
      ? withOfflineClientUuid(mutation.data, idKey)
      : mutation.data
     config.data =
      isQueuedCustomerCreate(mutation) &&
      replayData &&
      typeof replayData === 'object' &&
      !Array.isArray(replayData)
       ? { ...(replayData as Record<string, unknown>), sync_upsert: true }
       : replayData
    }
   }

   const method = mutation.method.toLowerCase() as 'post' | 'put' | 'delete' | 'patch'
   await axiosInstance({ method, url: mutation.url, ...config })
   done.add(mutation.id)
   succeededSlices.add(mutation.sliceName)
  } catch (error: any) {
   let replayError = error
   if (isQueuedCustomerCreate(mutation) && isCustomerPhoneDuplicateError(error)) {
    const existingId = Number(error?.response?.data?.existing_customer_id)
    if (Number.isFinite(existingId) && existingId > 0) {
     try {
      const raw =
       mutation.data && typeof mutation.data === 'object' && !Array.isArray(mutation.data)
        ? (mutation.data as Record<string, unknown>)
        : {}
      const {
       sync_upsert: _syncUpsert,
       client_uuid: _clientUuid,
       additional_phones: _additionalPhones,
       ...updateData
      } = raw
      await axiosInstance({
       method: 'put',
       url: `/customers/${existingId}`,
       data: updateData,
      })
      done.add(mutation.id)
      succeededSlices.add(mutation.sliceName)
      continue
     } catch (updateError: any) {
      replayError = updateError
     }
    }
   }

   const message =
    replayError?.response?.data?.message || replayError?.message || 'sync failed'
   const newCount = await offlineQueue.incrementRetry(mutation.id, message)
   errors.push(`${mutation.id}: ${message}`)
   if (newCount >= 5) permanentlyFailedSlices.add(mutation.sliceName)
  }
 }

 await offlineQueue.dequeueMany(done)

 const slicesToRefresh = new Set<string>()
 succeededSlices.forEach((name) => slicesToRefresh.add(name))
 permanentlyFailedSlices.forEach((name) => slicesToRefresh.add(name))
 if (slicesToRefresh.size > 0) {
  const { store: reduxStore } = await import('slices/index')
  const sliceRefreshMap: Record<string, () => void> = {
   categories: () => reduxStore.dispatch(fetchCategories({}) as any),
   products: () => reduxStore.dispatch(fetchProducts({}) as any),
   customers: () => reduxStore.dispatch(fetchCustomers({}) as any),
   suppliers: () => reduxStore.dispatch(fetchSuppliers({}) as any),
   purchases: () => reduxStore.dispatch(fetchPurchases({}) as any),
   expenses: () => reduxStore.dispatch(fetchExpenses({}) as any),
   deliveries: () => {
    // C8 fix: backend `today()` requires branch_id; in admin/global mode the
    // X-Branch-Id header is omitted by axiosConfig interceptor, causing silent 422.
    // Read current branch from Redux state and pass it explicitly when available.
    const reduxState = reduxStore.getState() as any
    const branchId: string | null = reduxState?.branch?.selectedBranchId ?? null
    reduxStore.dispatch(fetchDeliveries({}) as any)
    reduxStore.dispatch(fetchActiveDrivers({}) as any)
    if (branchId) {
     reduxStore.dispatch(fetchTodayDeliveries({ branch_id: branchId }) as any)
    }
   },
   deliveryZones: () => reduxStore.dispatch(fetchDeliveryZones({}) as any),
   driverSettlements: () => {
    // C7 fix: settlements mutate delivery rows server-side (collection_status='settled',
    // settlement_id, settled_at). Refresh deliveries so the local cache isn't stale.
    // C8 fix: both fetchDriverSettlements and fetchTodayDeliveries require branch_id.
    const reduxState = reduxStore.getState() as any
    const branchId: string | null = reduxState?.branch?.selectedBranchId ?? null
    if (branchId) {
     reduxStore.dispatch(fetchDriverSettlements({ branch_id: branchId }) as any)
     reduxStore.dispatch(fetchTodayDeliveries({ branch_id: branchId }) as any)
    }
    reduxStore.dispatch(fetchDeliveries({}) as any)
   },
  }
  slicesToRefresh.forEach((sliceName) => {
   try {
    sliceRefreshMap[sliceName]?.()
   } catch {}
  })
 }

 return { replayed: done.size, errors }
}
